import { Navigate } from 'react-router-dom';

import { useHasPermission } from '../../hooks';

interface PermissionGuardProps {
  requiredPermission: string | string[];
  children: React.ReactNode;
  options?: {
    operator?: 'AND' | 'OR';
  };
}

/**
 * A wrapper component that wrap the whole route with permission guard.
 * @returns Either the children or a navigate to access denied page.
 *
 * @example
 * ```tsx
 * <PermissionGuard requiredPermission={['exam.qconfig.view']}>
 *  <Outlet />
 * </PermissionGuard>
 * ```
 * // it will redirect to access denied page if the user does not have permission to view qconfig.
 */
export default function PermissionGuard(props: Readonly<PermissionGuardProps>) {
  const {
    requiredPermission,
    children,
    options = {
      operator: 'AND',
    },
  } = props;

  const hasPermission = useHasPermission(requiredPermission, options?.operator);

  if (hasPermission) {
    return children;
  }

  return <Navigate to='/access-denied' replace />;
}
